import { useEffect, useState } from 'react';
import { useApp } from '../../app/AppContext';
import { useStore } from '../../app/StoreContext';
import { callFn } from '../../lib/api';
import { allTimeZones } from '../../lib/types';
import { PauseControl } from '../../components/PauseControl';
import type { DeliverySettings, Hours, PaymentAccountStatus, WebDomainStatus } from '../../lib/types';

/** Dollars in the input, whole cents on the wire. */
function dollarsToCents(v: string): number | null {
  const t = v.trim();
  if (t === '') return 0;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}
function centsToDollars(c: number | null | undefined): string {
  return !c ? '' : (c / 100).toFixed(2);
}

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export function AdminSettings() {
  const { me, reload, toast } = useApp();
  const { reloadStore } = useStore();
  const [name, setName] = useState('');
  const [about, setAbout] = useState('');
  const [hours, setHours] = useState<Hours>({});
  const [taxPct, setTaxPct] = useState('0');
  const [busy, setBusy] = useState<string | null>(null);
  const [pay, setPay] = useState<PaymentAccountStatus | null>(null);
  const [secretKey, setSecretKey] = useState('');
  const [publishableKey, setPublishableKey] = useState('');
  const [domains, setDomains] = useState<WebDomainStatus[] | null>(null);
  // Delivery (D-P1)
  const [address, setAddress] = useState('');
  const [delEnabled, setDelEnabled] = useState(true);
  const [delFee, setDelFee] = useState('');
  const [delMin, setDelMin] = useState('');
  const [delRadius, setDelRadius] = useState('');
  const [delEta, setDelEta] = useState('');
  const [delZips, setDelZips] = useState<string[]>([]);
  const [zipDraft, setZipDraft] = useState('');
  const [tz, setTz] = useState('');

  useEffect(() => {
    if (!me) return;
    setName(me.displayName); setAbout(me.about || '');
    const h: Hours = {}; for (const d of DAYS) h[d] = me.hours?.[d] ?? me.hours?.[d.toLowerCase()] ?? '';
    for (const [k, v] of Object.entries(me.hours || {})) if (!(k in h) && !DAYS.some((d) => d.toLowerCase() === k.toLowerCase())) h[k] = v;
    setHours(h);
    setTaxPct((me.taxRateBps / 100).toString());
    setAddress(me.address || '');
    setDelEnabled(me.deliveryEnabled !== false);
    setDelFee(centsToDollars(me.deliveryFeeCents));
    setDelMin(centsToDollars(me.deliveryMinSubtotalCents));
    setDelRadius(me.deliveryRadiusMiles === null || me.deliveryRadiusMiles === undefined ? '' : String(me.deliveryRadiusMiles));
    setDelEta(me.deliveryEtaMinutes === null || me.deliveryEtaMinutes === undefined ? '' : String(me.deliveryEtaMinutes));
    setDelZips(Array.isArray(me.deliveryZips) ? me.deliveryZips : []);
    setTz(me.timezone || me.timezoneSuggestion?.timezone || '');
  }, [me]);
  useEffect(() => { callFn<PaymentAccountStatus>('payments_account_status').then(setPay).catch(() => setPay({ configured: false })); }, []);

  // Apple Pay on the web: the store's Stripe account must have this site's
  // domain registered. Re-check (and re-validate) whenever the card is open.
  const checkDomains = async () => {
    setBusy('domains');
    try { const r = await callFn<{ domains: WebDomainStatus[] }>('payments_web_domains'); setDomains(r.domains); }
    catch (e) { toast(e instanceof Error ? e.message : 'Could not check Apple Pay.', 'err'); }
    finally { setBusy(null); }
  };
  useEffect(() => { if (pay?.configured && pay.resolved !== 'app_default') void checkDomains(); }, [pay?.configured, pay?.resolved]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = async () => { await reload(); await reloadStore(); };
  const run = async (key: string, label: string, fn: () => Promise<unknown>) => {
    setBusy(key);
    try { await fn(); toast(label, 'ok'); await refresh(); }
    catch (e) { toast(e instanceof Error ? e.message : 'That did not work.', 'err'); }
    finally { setBusy(null); }
  };

  const saveGeneral = () => run('general', 'Saved', () => callFn('update_about', { displayName: name.trim(), about }));
  const saveHours = () => {
    const clean: Hours = {};
    for (const [k, v] of Object.entries(hours)) if (v.trim()) clean[k] = v.trim();
    if (Object.keys(clean).length === 0) { toast('Enter hours for at least one day.', 'err'); return; }
    return run('hours', 'Hours saved', () => callFn('update_hours', { hours: clean }));
  };
  const saveTax = () => {
    const bps = Math.round(Number(taxPct) * 100);
    if (!Number.isFinite(bps) || bps < 0 || bps > 5000) { toast('Tax rate must be between 0% and 50%.', 'err'); return; }
    return run('tax', 'Tax rate saved', () => callFn('update_tax_rate', { taxRateBps: bps }));
  };
  const saveAddress = () => run('address', 'Address saved', () => callFn('update_store_address', { address: address.trim() }));
  const saveTz = () => run('tz', 'Time zone saved', () => callFn('update_store_timezone', { timezone: tz.trim() || null }));

  const addZip = () => {
    const z = zipDraft.trim();
    if (!/^\d{5}$/.test(z)) { toast('A ZIP code is 5 digits.', 'err'); return; }
    if (!delZips.includes(z)) setDelZips([...delZips, z]);
    setZipDraft('');
  };

  const saveDelivery = () => {
    const feeCents = dollarsToCents(delFee);
    const minSubtotalCents = dollarsToCents(delMin);
    if (feeCents === null) { toast('The delivery fee must be a positive amount.', 'err'); return; }
    if (minSubtotalCents === null) { toast('The minimum order must be a positive amount.', 'err'); return; }
    const radius = delRadius.trim() === '' ? null : Number(delRadius);
    if (radius !== null && (!Number.isFinite(radius) || radius <= 0 || radius > 50)) {
      toast('Max distance must be between 0 and 50 miles.', 'err'); return;
    }
    const eta = delEta.trim() === '' ? null : Number(delEta);
    if (eta !== null && (!Number.isInteger(eta) || eta <= 0 || eta > 240)) {
      toast('Estimated time must be a whole number of minutes, up to 240.', 'err'); return;
    }
    return run('delivery', 'Delivery settings saved', () => callFn<{ delivery: DeliverySettings }>('update_delivery_settings', {
      enabled: delEnabled, provider: 'self', feeCents, minSubtotalCents,
      radiusMiles: radius, zips: delZips, etaMinutes: eta,
    }));
  };

  const connectStripe = async () => {
    const sk = secretKey.trim(), pk = publishableKey.trim();
    if (!sk.startsWith('sk_') && !sk.startsWith('rk_')) { toast('The secret key starts with sk_ (test keys: sk_test_…).', 'err'); return; }
    if (!pk.startsWith('pk_')) { toast('The publishable key starts with pk_.', 'err'); return; }
    setBusy('stripe');
    try {
      const r = await callFn<{ status: PaymentAccountStatus }>('payments_add_account', { secretKey: sk, publishableKey: pk, displayName: name.trim() || undefined });
      setPay(r.status); setSecretKey(''); setPublishableKey('');
      toast('Stripe connected.', 'ok'); await refresh();
    } catch (e) { toast(e instanceof Error ? e.message : 'Could not connect Stripe.', 'err'); }
    finally { setBusy(null); }
  };
  const disconnectStripe = async () => {
    if (!window.confirm('Disconnect Stripe? Customers will only be able to pay at the store.')) return;
    setBusy('stripe');
    try { const r = await callFn<{ status: PaymentAccountStatus }>('payments_remove_account'); setPay(r.status); toast('Stripe disconnected.', 'ok'); await refresh(); }
    catch (e) { toast(e instanceof Error ? e.message : 'Could not disconnect.', 'err'); }
    finally { setBusy(null); }
  };

  if (!me) return null;
  return (
    <div className="settings">
      <h1>Settings</h1>

      <section className="card">
        <h2>General</h2>
        <label>Store name<input className="input" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} /></label>
        <label>About<textarea className="input" rows={6} maxLength={4000} value={about} onChange={(e) => setAbout(e.target.value)} /></label>
        <p className="muted small">Your logo is chosen in the Media library.</p>
        <button className="btn" disabled={busy === 'general' || !name.trim()} onClick={() => void saveGeneral()}>Save</button>

        <div className="subcard">
          <h3>Store address</h3>
          <label>Street address<input className="input" maxLength={300} placeholder="123 Main St, Chalfont, PA 18914" value={address} onChange={(e) => setAddress(e.target.value)} /></label>
          <p className="muted small">Where orders are picked up from. Used for delivery distance and, later, for couriers.</p>
          <button className="btn btn-ghost btn-sm" disabled={busy === 'address'} onClick={() => void saveAddress()}>Save address</button>
        </div>

        <div className="subcard">
          <h3>Time zone</h3>
          <label>Your store&rsquo;s time zone
            <input className="input" list="tz-options" maxLength={64} placeholder="America/New_York"
                   value={tz} onChange={(e) => setTz(e.target.value)} />
          </label>
          <datalist id="tz-options">
            {allTimeZones().map((z) => <option key={z} value={z} />)}
          </datalist>
          {!me.timezone && me.timezoneSuggestion && (
            <p className="muted small">
              Suggested from your address ({me.timezoneSuggestion.state}): <code>{me.timezoneSuggestion.timezone}</code>.
              {me.timezoneSuggestion.confirmRequired
                ? ' That state spans two time zones — please check this is right before saving.'
                : ' Please confirm it and save.'}
            </p>
          )}
          <p className="muted small">
            Used for pause options like &ldquo;until tomorrow morning&rdquo;, so they mean your clock and not your customer&rsquo;s.
          </p>
          <button className="btn btn-ghost btn-sm" disabled={busy === 'tz'} onClick={() => void saveTz()}>Save time zone</button>
        </div>
      </section>

      <section className="card">
        <h2>Pause ordering</h2>
        <p className="muted small">
          For when something is off today but will be back. Customers see your note, and ordering resumes on its own.
        </p>
        <PauseControl channel="all" noun="all ordering" pause={me.pauses?.all}
                      placeholder="We're closed today — back tomorrow."
                      timezone={me.timezone} toast={toast} onDone={refresh} />
        {!me.pauses?.all && (
          <>
            <PauseControl channel="pickup" noun="pickup" pause={me.pauses?.pickup}
                          placeholder="No pickup today — delivery only."
                          timezone={me.timezone} toast={toast} onDone={refresh} />
            <PauseControl channel="delivery" noun="delivery" pause={me.pauses?.delivery}
                          placeholder="Delivery is not available today, but it will be tomorrow."
                          timezone={me.timezone} toast={toast} onDone={refresh} />
          </>
        )}
      </section>

      <section className="card">
        <h2>Delivery</h2>
        <label className="check">
          <input type="checkbox" checked={delEnabled} onChange={(e) => setDelEnabled(e.target.checked)} />
          {' '}This store offers delivery
        </label>
        <p className="muted small">Turn this off and customers only see Pickup — on the website, in the app, and in chat.</p>

        {delEnabled && (
          <>
            <div className="subcard">
              <h3>Fulfilled by</h3>
              <label className="check"><input type="radio" name="delprov" checked readOnly /> Our own driver</label>
              <label className="check muted"><input type="radio" name="delprov" disabled /> Uber Direct <span className="tag tag-muted">Coming soon</span></label>
              <label className="check muted"><input type="radio" name="delprov" disabled /> DoorDash Drive <span className="tag tag-muted">Coming soon</span></label>
              <p className="muted small">Courier dispatch is not available yet. Customers never choose the courier — you do.</p>
            </div>

            <div className="grid-2">
              <label>Delivery fee ($)<input className="input" inputMode="decimal" placeholder="0.00" value={delFee} onChange={(e) => setDelFee(e.target.value)} /></label>
              <label>Minimum order ($)<input className="input" inputMode="decimal" placeholder="0.00" value={delMin} onChange={(e) => setDelMin(e.target.value)} /></label>
            </div>
            <p className="muted small">The fee is added to every delivery order and shown to the customer as its own line. The minimum is checked against the order before tax.</p>

            <label>Estimated delivery time (minutes)<input className="input" style={{ maxWidth: 140 }} inputMode="numeric" placeholder="40" value={delEta} onChange={(e) => setDelEta(e.target.value)} /></label>

            <div className="subcard">
              <h3>Delivery area</h3>
              <label>Max distance from the store (miles)<input className="input" style={{ maxWidth: 140 }} inputMode="decimal" placeholder="5" value={delRadius} onChange={(e) => setDelRadius(e.target.value)} /></label>
              {!me.addressLat && delRadius.trim() !== '' && (
                <p className="muted small">Saved, but not enforced yet — we still need to locate your store address on the map.</p>
              )}
              <label>ZIP codes you deliver to
                <span className="row wrap" style={{ gap: 8 }}>
                  <input className="input" style={{ maxWidth: 140 }} inputMode="numeric" maxLength={5} placeholder="18914" value={zipDraft}
                         onChange={(e) => setZipDraft(e.target.value)}
                         onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addZip(); } }} />
                  <button type="button" className="btn btn-ghost btn-sm" onClick={addZip}>Add</button>
                </span>
              </label>
              <div className="row wrap" style={{ gap: 8, margin: '6px 0' }}>
                {delZips.map((z) => (
                  <span key={z} className="tag">{z}
                    <button type="button" className="btn btn-ghost btn-sm" aria-label={`Remove ${z}`}
                            onClick={() => setDelZips(delZips.filter((v) => v !== z))}>✕</button>
                  </span>
                ))}
                {delZips.length === 0 && <span className="muted small">No ZIP restriction — delivery is offered to any address.</span>}
              </div>
            </div>
          </>
        )}

        <button className="btn" disabled={busy === 'delivery'} onClick={() => void saveDelivery()}>Save delivery settings</button>
      </section>

      <section className="card">
        <h2>Hours</h2>
        <div className="hours-edit">
          {Object.keys(hours).map((d) => (
            <label key={d}><span>{d}</span><input className="input" maxLength={120} placeholder="Closed or e.g. 11:00 AM – 9:00 PM" value={hours[d]} onChange={(e) => setHours({ ...hours, [d]: e.target.value })} /></label>
          ))}
        </div>
        <button className="btn" disabled={busy === 'hours'} onClick={() => void saveHours()}>Save hours</button>
      </section>

      <section className="card">
        <h2>Sales tax</h2>
        <label>Tax rate (%)<input className="input" style={{ maxWidth: 140 }} inputMode="decimal" value={taxPct} onChange={(e) => setTaxPct(e.target.value)} /></label>
        <p className="muted small">Applied to every order's subtotal. Leave 0 if prices already include tax.</p>
        <button className="btn" disabled={busy === 'tax'} onClick={() => void saveTax()}>Save tax rate</button>
      </section>

      <section className="card">
        <h2>Online payments (Stripe)</h2>
        {pay?.configured ? (
          <>
            <p>Connected{pay.displayName ? ` — ${pay.displayName}` : ''}{pay.providerAccountId ? ` (${pay.providerAccountId})` : ''}.{pay.livemode === false && ' Test mode.'}{pay.resolved === 'app_default' && ' Using the platform default account.'}</p>
            {pay.resolved !== 'app_default' && <button className="btn btn-ghost" disabled={busy === 'stripe'} onClick={() => void disconnectStripe()}>Disconnect</button>}
            {pay.resolved !== 'app_default' && (
              <div className="subcard">
                <h3>Apple Pay</h3>
                <p className="muted small">In the app: your Stripe account needs an Apple Pay certificate for the StoreFront merchant ID (Stripe Dashboard → Settings → Payment methods → Apple Pay → iOS certificates). On this website: the domain below must be verified on your account — that happens automatically; check again if it shows a problem.</p>
                {domains === null && <p className="muted small">Checking…</p>}
                {domains && domains.length === 0 && <p className="muted small">No web domains are configured on this platform.</p>}
                {domains && domains.map((d) => (
                  <div key={d.domain} className="row wrap" style={{ gap: 8, margin: '6px 0' }}>
                    <code>{d.domain}</code>
                    {d.applePay === 'active' ? <span className="tag tag-ok">Apple Pay ready</span> : d.applePay === 'inactive' ? <span className="tag tag-warn">Not verified</span> : <span className="tag tag-muted">Error</span>}
                    {d.error && <span className="muted small">{d.error}</span>}
                  </div>
                ))}
                <button className="btn btn-ghost btn-sm" disabled={busy === 'domains'} onClick={() => void checkDomains()}>{busy === 'domains' ? 'Checking…' : 'Check again'}</button>
              </div>
            )}
          </>
        ) : (
          <>
            <p className="muted">Paste the keys from your Stripe dashboard (Developers → API keys). The secret key is stored write-only on the platform and never shown again.{pay?.reason ? ` (${pay.reason})` : ''}</p>
            <label>Secret key<input className="input" type="password" autoComplete="off" placeholder="sk_test_…" value={secretKey} onChange={(e) => setSecretKey(e.target.value)} /></label>
            <label>Publishable key<input className="input" autoComplete="off" placeholder="pk_test_…" value={publishableKey} onChange={(e) => setPublishableKey(e.target.value)} /></label>
            <button className="btn" disabled={busy === 'stripe' || !secretKey.trim() || !publishableKey.trim()} onClick={() => void connectStripe()}>{busy === 'stripe' ? 'Connecting…' : 'Connect Stripe'}</button>
          </>
        )}
      </section>
    </div>
  );
}
